import { createContext, useContext } from 'react';
import type { LanguageCode } from '@ampshift/shared';

export type Lang = 'en' | 'ar';

const STRINGS = {
  appName: { en: 'AmpShift Patient', ar: 'أمبشيفت للمرضى' },
  syntheticBanner: { en: 'Demo environment — synthetic data only', ar: 'بيئة تجريبية — بيانات اصطناعية فقط' },
  signIn: { en: 'Sign in', ar: 'تسجيل الدخول' },
  signOut: { en: 'Sign out', ar: 'تسجيل الخروج' },
  email: { en: 'Email', ar: 'البريد الإلكتروني' },
  password: { en: 'Password', ar: 'كلمة المرور' },
  signingIn: { en: 'Signing in…', ar: 'جارٍ تسجيل الدخول…' },
  devSignIn: { en: 'Signing in as the demo patient…', ar: 'جارٍ تسجيل الدخول كمريض تجريبي…' },
  language: { en: 'العربية', ar: 'English' },
  theme: { en: 'Appearance', ar: 'المظهر' },
  themeSystem: { en: 'Match device', ar: 'حسب الجهاز' },
  themeLight: { en: 'Light', ar: 'فاتح' },
  themeDark: { en: 'Dark', ar: 'داكن' },
  chatTitle: { en: 'Ask about your care', ar: 'اسأل عن رعايتك' },
  chatPlaceholder: { en: 'e.g. "When is my next appointment?" or "Show my latest results"', ar: 'مثال: "متى موعدي القادم؟" أو "اعرض آخر نتائجي"' },
  send: { en: 'Send', ar: 'إرسال' },
  sending: { en: 'Sending…', ar: 'جارٍ الإرسال…' },
  myAppointments: { en: 'My appointments', ar: 'مواعيدي' },
  noAppointments: { en: 'No upcoming appointments.', ar: 'لا توجد مواعيد قادمة.' },
  cancel: { en: 'Cancel', ar: 'إلغاء' },
  reschedule: { en: 'Reschedule', ar: 'إعادة جدولة' },
  confirmAttendance: { en: 'Confirm attendance', ar: 'تأكيد الحضور' },
  attendanceConfirmed: { en: 'Attendance confirmed', ar: 'تم تأكيد الحضور' },
  bookTitle: { en: 'Book an appointment', ar: 'حجز موعد' },
  provider: { en: 'Clinician', ar: 'الطبيب' },
  anyProvider: { en: 'Any clinician', ar: 'أي طبيب' },
  appointmentType: { en: 'Visit type', ar: 'نوع الزيارة' },
  typeFollowUp: { en: 'Follow-up (15 min)', ar: 'متابعة (15 دقيقة)' },
  typeNew: { en: 'New visit (30 min)', ar: 'زيارة جديدة (30 دقيقة)' },
  date: { en: 'From date', ar: 'من تاريخ' },
  findSlots: { en: 'Find available times', ar: 'ابحث عن الأوقات المتاحة' },
  searching: { en: 'Searching…', ar: 'جارٍ البحث…' },
  noSlots: { en: 'No available times in that range.', ar: 'لا توجد أوقات متاحة في هذه الفترة.' },
  pickSlot: { en: 'Choose a time', ar: 'اختر وقتاً' },
  confirm: { en: 'Confirm', ar: 'تأكيد' },
  reject: { en: 'Go back', ar: 'رجوع' },
  confirming: { en: 'Confirming…', ar: 'جارٍ التأكيد…' },
  booked: { en: 'Your appointment is booked.', ar: 'تم حجز موعدك.' },
  cancelled: { en: 'Your appointment was cancelled.', ar: 'تم إلغاء موعدك.' },
  rescheduled: { en: 'Your appointment was moved.', ar: 'تم نقل موعدك.' },
  cancelAppointmentTitle: { en: 'Cancel this appointment?', ar: 'إلغاء هذا الموعد؟' },
  rescheduleTitle: { en: 'Move this appointment?', ar: 'نقل هذا الموعد؟' },
  bookingTitle: { en: 'Book this appointment?', ar: 'حجز هذا الموعد؟' },
  recordsTitle: { en: 'My results & records', ar: 'نتائجي وسجلاتي' },
  results: { en: 'Latest results', ar: 'أحدث النتائج' },
  medications: { en: 'Medications', ar: 'الأدوية' },
  documents: { en: 'Documents', ar: 'المستندات' },
  noResults: { en: 'No results recorded.', ar: 'لا توجد نتائج مسجلة.' },
  noMedications: { en: 'No medications recorded.', ar: 'لا توجد أدوية مسجلة.' },
  noDocuments: { en: 'No documents recorded.', ar: 'لا توجد مستندات مسجلة.' },
  test: { en: 'Test', ar: 'الفحص' },
  value: { en: 'Value', ar: 'القيمة' },
  reference: { en: 'Reference', ar: 'المرجع' },
  flag: { en: 'Flag', ar: 'الإشارة' },
  collected: { en: 'Collected', ar: 'تاريخ السحب' },
  open: { en: 'Open', ar: 'فتح' },
  loading: { en: 'Loading…', ar: 'جارٍ التحميل…' },
  error: { en: 'Something went wrong.', ar: 'حدث خطأ ما.' },
  retry: { en: 'Retry', ar: 'إعادة المحاولة' },
  status: { en: 'Status', ar: 'الحالة' },
  pending: { en: 'Awaiting confirmation', ar: 'بانتظار التأكيد' },
  expires: { en: 'Expires', ar: 'ينتهي' },
  youSaid: { en: 'You', ar: 'أنت' },
  assistant: { en: 'AmpShift', ar: 'أمبشيفت' },
  aiNarrative: { en: 'AI-generated summary', ar: 'ملخص مولّد بالذكاء الاصطناعي' },
  verified: { en: 'From your record', ar: 'من سجلك' },
  provenancePatient: { en: 'patient-reported', ar: 'مبلّغ من المريض' },
} as const;

export type StringKey = keyof typeof STRINGS;

export function translate(lang: Lang, key: StringKey): string {
  return STRINGS[key][lang];
}

export function toLanguageCode(lang: Lang): LanguageCode {
  return lang === 'ar' ? 'ar-SA' : 'en-US';
}

/** Gregorian calendar with Latin digits in both languages so dates are unambiguous. */
export function intlLocale(lang: Lang): string {
  return lang === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB';
}

export function formatDateTime(iso: string, lang: Lang, timeZone = 'Asia/Riyadh'): string {
  return new Intl.DateTimeFormat(intlLocale(lang), { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(iso));
}

export function formatDate(iso: string, lang: Lang, timeZone = 'Asia/Riyadh'): string {
  return new Intl.DateTimeFormat(intlLocale(lang), { dateStyle: 'medium', timeZone }).format(new Date(iso));
}

export interface I18n {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  t: (key: StringKey) => string;
  setLang: (lang: Lang) => void;
}

export const I18nContext = createContext<I18n>({ lang: 'en', dir: 'ltr', t: (k) => translate('en', k), setLang: () => undefined });

export function useI18n(): I18n {
  return useContext(I18nContext);
}
